// Tools on the round step 「不合适 3 天内复盘 / 有好感约第二次」 (服务进度):
//   · 写 3 天复盘 — structured review (reasons + what they liked / why not a fit
//     / how the portrait changes) submitted as the step's 'review' outcome,
//     which ends this round; the next round starts at 两位老师核实.
//   · 更新推荐状态 — the round's recommendations with their match status;
//     once every one of them is 已拒绝 the coach can close the round straight
//     into the next round of recommendations.
// Both write through the existing APIs (sop-steps tick, PUT /matches/:id), so
// round bookkeeping stays in the backend.
import React, { useMemo, useState } from 'react';
import { PenLine, RefreshCw, ArrowRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { workspaceAPI, matchesAPI } from '../../../services/api';
import { S, btnPrimary, btnGhost, field } from './wsShared';

const REASONS = ['外形', '年龄感', '身高', '性格', '沟通', '价值观', '工作 / 收入', '地域', '其他'];
const STATUS = [
  ['pending', '待确认'], ['approved', '已确认'], ['in_contact', '接触中'], ['rejected', '已拒绝'],
  ['succeeded', '已约见'], ['in_relationship', '已交往'], ['failed', '未成功'], ['cancelled', '已取消'], ['completed', '已完成'],
];
const statusLabel = (v) => (STATUS.find((x) => x[0] === v) || [v, v || '—'])[1];
const allRejected = (list) => list.length > 0 && list.every((m) => m.status === 'rejected');

export default function ReviewRoundTools({ profileId, round, roundMatches = [], onChanged }) {
  const [panel, setPanel] = useState(null); // 'review' | 'status' | null
  const [busy, setBusy] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [liked, setLiked] = useState('');
  const [why, setWhy] = useState('');
  const [adjust, setAdjust] = useState('');
  // Local copy so a status change shows at once; the parent reload refreshes it.
  const [local, setLocal] = useState(null);
  const matches = useMemo(() => local || roundMatches, [local, roundMatches]);
  const nextRound = Number(round || 1) + 1;

  const endRound = async (note) => {
    setBusy(true);
    try {
      await workspaceAPI.tickStep(profileId, 'review_or_second', { round, choice: 'review', note: note || undefined });
      toast.success(`第 ${round} 轮已复盘，开始第 ${nextRound} 轮推荐`);
      setPanel(null); setLocal(null);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || '提交失败');
    } finally { setBusy(false); }
  };

  const submitReview = async () => {
    if (!why.trim() && !reasons.length) { toast.error('请写不合适的原因，或至少选一个原因'); return; }
    const lines = [
      reasons.length ? `原因：${reasons.join('、')}` : '',
      liked.trim() ? `喜欢的地方：${liked.trim()}` : '',
      why.trim() ? `不合适 / 复盘：${why.trim()}` : '',
      adjust.trim() ? `下一轮画像调整：${adjust.trim()}` : '',
    ].filter(Boolean);
    // eslint-disable-next-line no-alert
    if (!window.confirm(`提交复盘后，第 ${round} 轮结束，进入第 ${nextRound} 轮推荐。确定提交？`)) return;
    await endRound(lines.join('\n'));
  };

  const setStatus = async (m, status) => {
    if (status === m.status) return;
    setBusy(true);
    try {
      await matchesAPI.update(m.id, { status });
      const next = matches.map((x) => (x.id === m.id ? { ...x, status } : x));
      setLocal(next);
      toast.success(`${m.other_name || '推荐'}：${statusLabel(status)}`);
      if (allRejected(next)) {
        const names = next.map((x) => `${x.other_name || ''}${x.other_offline_id ? ` #${x.other_offline_id}` : ''}`).join('、');
        // eslint-disable-next-line no-alert
        if (window.confirm(`第 ${round} 轮推荐的 ${next.length} 位都已拒绝。现在结束本轮，进入第 ${nextRound} 轮推荐？`)) {
          setBusy(false);
          await endRound(`本轮推荐全部已拒绝：${names}`);
          return;
        }
      }
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || '更新失败');
    } finally { setBusy(false); }
  };

  const tab = (key, Icon, label) => (
    <button type="button" onClick={() => setPanel(panel === key ? null : key)} aria-expanded={panel === key}
      style={{ ...(panel === key ? btnPrimary(false) : btnGhost), display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Icon size={14} strokeWidth={1.5} aria-hidden="true" />{label}
    </button>
  );
  const box = { border: `1px solid ${S.line}`, borderRadius: 2, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, background: '#FFFDF9' };
  const lbl = { fontSize: 12.5, color: S.sub, display: 'flex', flexDirection: 'column', gap: 4 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {tab('review', PenLine, '写 3 天复盘')}
        {tab('status', RefreshCw, `更新推荐状态${matches.length ? `（${matches.length}）` : ''}`)}
      </div>

      {panel === 'review' && (
        <div style={box}>
          <div style={{ fontSize: 12.5, color: S.sub }}>见面后 3 天内复盘：喜欢与拒绝的原因要具体，用来更新画像。提交后第 {round} 轮结束，进入第 {nextRound} 轮推荐。</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="group" aria-label="不合适的原因">
            {REASONS.map((r) => {
              const on = reasons.includes(r);
              return (
                <button key={r} type="button" aria-pressed={on} onClick={() => setReasons((x) => (on ? x.filter((y) => y !== r) : [...x, r]))}
                  style={{ height: 32, padding: '0 12px', borderRadius: 2, border: `1px solid ${on ? '#111111' : S.line}`, background: on ? '#111111' : '#FFFDF9', color: on ? '#FFFDF9' : S.ink, fontFamily: 'inherit', fontSize: 12.5, cursor: 'pointer' }}>
                  {r}
                </button>
              );
            })}
          </div>
          <label style={lbl}>喜欢的地方（选填）
            <textarea value={liked} onChange={(e) => setLiked(e.target.value)} rows={2} style={{ ...field, height: 'auto', padding: 8, resize: 'vertical' }} />
          </label>
          <label style={lbl}>不合适的原因 / 复盘内容
            <textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={3} style={{ ...field, height: 'auto', padding: 8, resize: 'vertical' }} />
          </label>
          <label style={lbl}>下一轮画像怎么调整（选填）
            <textarea value={adjust} onChange={(e) => setAdjust(e.target.value)} rows={2} style={{ ...field, height: 'auto', padding: 8, resize: 'vertical' }} />
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setPanel(null)} style={btnGhost} disabled={busy}>取消</button>
            <button type="button" onClick={submitReview} style={btnPrimary(busy)} disabled={busy}>{busy ? '提交中…' : '提交复盘'}</button>
          </div>
        </div>
      )}

      {panel === 'status' && (
        <div style={box}>
          {!matches.length ? (
            <div style={{ fontSize: 13, color: S.sub }}>这一轮还没有推荐记录。</div>
          ) : (
            <>
              <div style={{ fontSize: 12.5, color: S.sub }}>第 {round} 轮推荐了 {matches.length} 位。全部设为「已拒绝」后，可以直接结束本轮、进入第 {nextRound} 轮推荐。</div>
              {matches.map((m) => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 0', borderTop: `1px solid ${S.line}` }}>
                  <span style={{ flex: '1 1 160px', minWidth: 0, fontSize: 14, color: S.ink }}>
                    {m.other_name || '—'}{m.other_offline_id && <span style={{ color: S.sub, fontSize: 12.5 }}> #{m.other_offline_id}</span>}
                  </span>
                  <select value={m.status || ''} disabled={busy} onChange={(e) => setStatus(m, e.target.value)}
                    aria-label={`${m.other_name || '推荐'} 的状态`} style={{ ...field, width: 140, color: m.status === 'rejected' ? '#9E2B25' : S.ink }}>
                    {!STATUS.some((x) => x[0] === m.status) && <option value={m.status || ''}>{statusLabel(m.status)}</option>}
                    {STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ))}
              {allRejected(matches) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', background: '#EFEAE2', borderRadius: 2 }}>
                  <span style={{ flex: 1, fontSize: 13, color: S.ink }}>本轮推荐都已拒绝。</span>
                  <button type="button" disabled={busy} style={{ ...btnPrimary(busy), display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => endRound(`本轮推荐全部已拒绝：${matches.map((x) => `${x.other_name || ''}${x.other_offline_id ? ` #${x.other_offline_id}` : ''}`).join('、')}`)}>
                    进入第 {nextRound} 轮推荐<ArrowRight size={14} strokeWidth={1.5} aria-hidden="true" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
